/**
 * CategoryPage Component
 * Displays products within a specific category with filtering and sorting
 * Uses infinite scroll to load more products as user scrolls
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Dropdown } from 'react-bootstrap';
import { useCategories, useInfiniteProducts } from '../../hooks/useProducts.js';
import { useMedRecFilter } from '../../context/MedRecFilterContext.jsx';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import ProductCard from '../../components/products/ProductCard.jsx';
import { DEFAULT_PRODUCT_SORT } from '../../constants/productSort.js';

const CategoryPage = () => {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get Med/Rec filter from global context
  const { filterValues } = useMedRecFilter();

  // Get URL parameters
  const initialSort = searchParams.get('sort') || DEFAULT_PRODUCT_SORT;
  const initialOrder = searchParams.get('order') || 'asc';
  const initialInStock = searchParams.get('inStock') === 'true';

  // Local state
  const [sortBy, setSortBy] = useState(initialSort);
  const [sortDirection, setSortDirection] = useState(initialOrder);
  const [inStockOnly, setInStockOnly] = useState(initialInStock);

  // Infinite scroll sentinel ref
  const sentinelRef = useRef(null);

  // Find category by slug first to get ID
  const { data: categories = [] } = useCategories();
  const category = categories.find(cat => cat.slug === slug);
  const categoryId = category?.id;

  // Get products for this category with infinite scroll
  const {
    products,
    pagination,
    isLoading: productsLoading,
    error: productsError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchProducts
  } = useInfiniteProducts({
    categoryId,
    pageSize: 24,
    sortBy,
    sortDirection,
    inStock: inStockOnly,
    isMed: filterValues.isMed,
    isRec: filterValues.isRec,
    enabled: !!categoryId,
  });

  // Intersection Observer for infinite scroll
  const handleObserver = useCallback((entries) => {
    const [entry] = entries;
    if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(handleObserver, {
      rootMargin: '600px',
    });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleObserver]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (sortBy !== DEFAULT_PRODUCT_SORT) params.set('sort', sortBy);
    if (sortDirection !== 'asc') params.set('order', sortDirection);
    if (inStockOnly) params.set('inStock', 'true');

    setSearchParams(params);
  }, [sortBy, sortDirection, inStockOnly, setSearchParams]);

  // Handle sort change
  const handleSortChange = (newSortBy, newDirection = 'asc') => {
    setSortBy(newSortBy);
    setSortDirection(newDirection);
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'inStock':
        setInStockOnly(value);
        break;
      default:
        break;
    }
  };

  // Loading states
  if (!categories.length) {
    return (
      <div className="bg-light min-vh-100">
        <Container fluid className="container-xxl py-5">
          <div className="text-center">
            <LoadingSpinner />
            <p className="mt-3 text-muted">Loading category...</p>
          </div>
        </Container>
      </div>
    );
  }

  // Error states - category not found by slug
  if (!category) {
    return (
      <div className="bg-light min-vh-100">
        <Container fluid className="container-xxl py-5">
          <div className="text-center">
            <div className="alert alert-danger">
              <h4>Category Not Found</h4>
              <p>The category "{slug}" could not be found.</p>
              <Link to="/categories" className="btn btn-primary">
                View All Categories
              </Link>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="bg-light min-vh-100">
      {/* Category Header */}
      <section className="bg-white py-5 border-bottom">
        <Container fluid className="">
          <Row className="align-items-center">
            <Col md={8}>
              {/* Breadcrumb */}
              <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to="/" className="text-decoration-none">Home</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/categories" className="text-decoration-none">Categories</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {category.name}
                  </li>
                </ol>
              </nav>

              {/* Category Info */}
              <div className="d-flex align-items-center mb-2">
                <div>
                  <h1 className="h2 mb-0">{category.name}</h1>
                  <p className="text-muted mb-0">
                    {pagination.totalItems || 0} products available
                  </p>
                </div>
              </div>
            </Col>

            <Col md={4} className="text-md-end">
              <Link to="/categories" className="btn btn-outline-primary">
                <i className="bi bi-arrow-left me-1"></i>
                All Categories
              </Link>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Filters and Controls */}
      <section className="bg-white py-3 border-bottom">
        <Container fluid className="">
          <Row className="align-items-center">
            <Col md={6}>
              {/* Filters */}
              <div className="d-flex align-items-center">
                <Form.Check
                  type="checkbox"
                  id="inStockFilter"
                  label="In Stock Only"
                  checked={inStockOnly}
                  onChange={(e) => handleFilterChange('inStock', e.target.checked)}
                  className="me-4"
                />
              </div>
            </Col>

            <Col md={6}>
              {/* Sort Controls */}
              <div className="d-flex align-items-center justify-content-md-end">
                <span className="me-3 text-muted">Sort by:</span>

                <Dropdown align="end">
                  <Dropdown.Toggle variant="outline-secondary" size="sm">
                    {sortBy.includes('brandDisplayOrder') && 'Default'}
                    {sortBy === 'name' && 'Name'}
                    {sortBy === 'price' && 'Price'}
                    {sortBy === 'newest' && 'Newest'}
                    <i className={`bi bi-arrow-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                  </Dropdown.Toggle>

                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => handleSortChange(DEFAULT_PRODUCT_SORT, 'asc')}>
                      Default
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={() => handleSortChange('name', 'asc')}>
                      Name (A-Z)
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleSortChange('name', 'desc')}>
                      Name (Z-A)
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={() => handleSortChange('price', 'asc')}>
                      Price (Low to High)
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleSortChange('price', 'desc')}>
                      Price (High to Low)
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={() => handleSortChange('newest', 'desc')}>
                      Newest First
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Products Grid */}
      <section className="py-4">
        <Container fluid className="">
          {productsLoading ? (
            <div className="text-center py-5">
              <LoadingSpinner />
              <p className="mt-3 text-muted">Loading products...</p>
            </div>
          ) : productsError ? (
            <div className="text-center py-5">
              <div className="alert alert-warning">
                <h5>Error Loading Products</h5>
                <p>{productsError.message}</p>
                <Button variant="primary" onClick={() => refetchProducts()}>
                  Try Again
                </Button>
              </div>
            </div>
          ) : products.length > 0 ? (
            <>
              <div className="product-grid">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>

              {/* Infinite scroll sentinel */}
              <div ref={sentinelRef} className="text-center py-4">
                {isFetchingNextPage && (
                  <>
                    <LoadingSpinner />
                    <p className="mt-2 text-muted">Loading more products...</p>
                  </>
                )}
                {!hasNextPage && products.length > 24 && (
                  <small className="text-muted">
                    Showing all {pagination.totalItems} products
                  </small>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-5">
              <i className="bi bi-box-seam display-1 text-muted mb-3"></i>
              <h3 className="text-muted">No Products Found</h3>
              <p className="text-muted">
                {inStockOnly
                  ? 'No products are currently in stock for this category.'
                  : 'This category doesn\'t have any products yet.'
                }
              </p>
              {inStockOnly && (
                <Button
                  variant="outline-primary"
                  onClick={() => handleFilterChange('inStock', false)}
                >
                  Show All Products
                </Button>
              )}
            </div>
          )}
        </Container>
      </section>
    </div>
  );
};

export default CategoryPage;